import React, { useState, useCallback } from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import useNotification from '../../../atoms/Notification/useNotification';
import Search from '../../../atoms/Icon/Search';
import Bookmark from '../../../atoms/Icon/Bookmark';
import { AgenticSearchInputProps } from '../AskAsperAgentic.types';
import Send from '../../../atoms/Icon/Send';
import QuestionCircleOutlined from '../../../atoms/Icon/QuestionCircleOutlined';
import Tooltip from '../../../atoms/Tooltip/Tooltip';

/**
 * AgenticSearchInput - Search input box with Question Bank and Save as Custom buttons
 * Part of the AskAsper Agentic Platform components
 */
const AgenticSearchInput: React.FC<AgenticSearchInputProps> = ({
    value: controlledValue,
    onChange,
    onSubmit,
    onQuestionBankClick,
    onSaveAsCustom,
    placeholder = 'Ask anything...',
    isLoading = false,
    disabled = false,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const { success } = useNotification();
    const [internalValue, setInternalValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    // Use controlled or uncontrolled value
    const inputValue = controlledValue !== undefined ? controlledValue : internalValue;
    const hasValue = inputValue && inputValue.trim().length > 0;
    const canSubmit = hasValue && !isLoading && !disabled;

    const handleInputChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = e.target.value;
            if (onChange) {
                onChange(newValue);
            } else {
                setInternalValue(newValue);
            }
        },
        [onChange],
    );

    const handleSubmit = useCallback(() => {
        if (canSubmit && onSubmit) {
            onSubmit(inputValue);
        }
    }, [canSubmit, onSubmit, inputValue]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit],
    );

    const handleSaveAsCustom = useCallback(() => {
        if (hasValue && onSaveAsCustom) {
            onSaveAsCustom(inputValue);
            success({
                message: 'Question saved',
                description: 'Your custom question has been saved successfully.',
                placement: 'bottomRight',
            });
        }
    }, [hasValue, onSaveAsCustom, inputValue, success]);

    return (
        <ConfigProvider theme={theme}>
            <div
                className={`bg-white border border-white rounded-[15px] flex flex-col h-[220px] justify-between p-5 w-full max-w-[764px] ${
                    isFocused
                        ? 'shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.7)]'
                        : hasValue
                          ? 'shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.5)]'
                          : 'shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.32)]'
                }`}
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-search-input
            >
                {/* Search Input Area */}
                <div className="flex gap-2 items-start py-[7px] w-full">
                    <div className="shrink-0">
                        <Search size={20} color="rgba(0,0,0,0.85)" />
                    </div>
                    <textarea
                        className="flex-1 font-['Open_Sans'] font-normal text-base leading-6 text-[rgba(0,0,0,0.88)] placeholder:text-[rgba(0,0,0,0.45)] border-0 outline-none resize-none bg-transparent min-h-[80px]"
                        placeholder={placeholder}
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        disabled={disabled || isLoading}
                        data-test-id={`${dataTestId}-textarea`}
                    />
                </div>

                {/* Bottom Actions */}
                <div className="flex items-center justify-between w-full gap-2">
                    {/* Left Actions */}
                    <div className="flex gap-2.5 items-center min-w-0">
                        {/* Question Bank Button */}
                        <Tooltip
                            title="Browse sample questions"
                            placement="top"
                            getPopupContainer={() => document.body}
                        >
                            <button
                                type="button"
                                className="bg-white border border-[#d0d5f5] rounded-lg flex gap-2 items-center h-10 px-4 cursor-pointer hover:bg-[#fafafa] transition-colors overflow-hidden relative whitespace-nowrap"
                                onClick={onQuestionBankClick}
                                data-test-id={`${dataTestId}-question-bank`}
                                aria-label="Browse sample questions"
                            >
                                <span className="shrink-0">
                                    <QuestionCircleOutlined size={16} color="#3b2c77" />
                                </span>
                                <span className="font-['Open_Sans'] font-normal text-sm leading-6 text-[#3b2c77] relative z-10 truncate">
                                    Question Bank
                                </span>
                            </button>
                        </Tooltip>

                        {/* Save as Custom Button */}
                        <Tooltip title="Save as template" placement="top" getPopupContainer={() => document.body}>
                            <span className="inline-flex">
                                <button
                                    type="button"
                                    className={`bg-white border border-[#e8eafa] rounded-lg flex gap-2 items-center h-10 px-4 transition-colors overflow-hidden relative whitespace-nowrap ${
                                        hasValue ? 'cursor-pointer hover:bg-[#fafafa]' : 'cursor-not-allowed opacity-60'
                                    }`}
                                    onClick={handleSaveAsCustom}
                                    disabled={!hasValue}
                                    data-test-id={`${dataTestId}-save-custom`}
                                    aria-label="Save as template"
                                >
                                    <span className="shrink-0">
                                        <Bookmark size={16} color={hasValue ? '#3B2C77' : 'rgba(0,0,0,0.25)'} />
                                    </span>
                                    <span
                                        className={`font-['Open_Sans'] font-normal text-sm leading-6 relative z-10 truncate ${
                                            hasValue ? 'text-[#3b2c77]' : 'text-[rgba(0,0,0,0.25)]'
                                        }`}
                                    >
                                        Save as custom
                                    </span>
                                </button>
                            </span>
                        </Tooltip>
                    </div>

                    {/* Send Button */}
                    <button
                        type="button"
                        className={`flex items-center gap-0.5 rounded px-3 py-1 pl-3 pr-2.5 transition-colors shrink-0 ${
                            canSubmit ? 'bg-[#3B2C77] cursor-pointer hover:bg-gray-800' : 'bg-black/50 cursor-not-allowed'
                        }`}
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        data-test-id={`${dataTestId}-send`}
                    >
                        <span className="font-['Open_Sans'] font-normal text-base leading-6 text-[#fbfaff] pr-1">Send</span>
                        <Send size={20} color="#fbfaff" />
                    </button>
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AgenticSearchInput;
